import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
import { motion } from 'framer-motion';
import {
  MessageCircle,
  Phone,
  Globe,
  Zap,
  ArrowRight,
  ChevronRight,
  MapPin,
  Paintbrush,
  Wrench,
  Rocket,
  ExternalLink,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Navigation from './components/Navigation';
import ContactForm from './pages/ContactForm';
import AboutPage from './pages/AboutPage';
import ConvoCoreWidget from './components/VoiceflowWidget';
import ContactPopup from './components/ContactPopup';
import { SparklesCore } from './components/ui/sparkles';

// ─── Animation variants ──────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: d } }),
};

const flyInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: (d = 0) => ({
    opacity: 1, x: 0,
    transition: { duration: 0.75, delay: d, ease: [0.16, 1, 0.3, 1] },
  }),
};

const flyInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: (d = 0) => ({
    opacity: 1, x: 0,
    transition: { duration: 0.75, delay: d, ease: [0.16, 1, 0.3, 1] },
  }),
};

const flyInUp = {
  hidden: { opacity: 0, y: 70 },
  visible: (d = 0) => ({
    opacity: 1, y: 0,
    transition: { type: 'spring', stiffness: 200, damping: 22, delay: d },
  }),
};

const popIn = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: (d = 0) => ({
    opacity: 1, scale: 1,
    transition: { type: 'spring', stiffness: 320, damping: 18, delay: d },
  }),
};

// ─── Data ────────────────────────────────────────────────────────────────────

const slides = [
  {
    title: 'Donna Tona Werkz Inc.',
    subtitle: 'Emergency Management & Crisis Response',
    image: '/images/Screenshot_2026-09-16_073401 copy.png',
    url: 'https://werkz.ca/',
  },
  {
    title: 'AURADRIVE',
    subtitle: 'Luxury Car Rental Website',
    image: '/images/Screenshot 2025-09-29 201805.png',
    url: 'https://auradrive-luxury-car-npw7.bolt.host/',
  },
  {
    title: 'LuxBuild',
    subtitle: 'Construction & Architecture Website',
    image: '/images/Screenshot 2025-09-29 162507 copy copy copy.png',
    url: 'https://luxbuild-high-end-co-yu72.bolt.host/',
  },
  {
    title: 'EnergyCorp',
    subtitle: 'Global Energy Solutions',
    image: '/images/Screenshot 2025-09-29 162941.png',
    url: 'https://professional-oil-gas-ejhm.bolt.host/',
  },
  {
    title: 'Snow Board Shop',
    subtitle: 'Freestyle • No Rules',
    image: '/images/screenshot_2026-01-16_215815.png',
    url: 'https://rumor-luxury-demo-sn-jnes.bolt.host/',
  },
];

const steps = [
  { icon: MapPin, num: '01', title: 'Map the Workflow', body: 'We start by understanding how your business actually runs — where leads come in, what gets dropped, and what needs to change.' },
  { icon: Paintbrush, num: '02', title: 'Design the Experience', body: 'We design each touchpoint: the website, the chat flow, the phone script, and the automations that tie it together.' },
  { icon: Wrench, num: '03', title: 'Build the System', body: 'We build and connect everything — no half-finished integrations. You get a complete, tested system before launch.' },
  { icon: Rocket, num: '04', title: 'Improve After Launch', body: 'After launch we monitor performance, tweak flows, and keep the system sharp as your business grows.' },
];

const clients = [
  'Contractors', 'Clinics', 'Salons', 'Trades', 'Restaurants', 'Service Businesses',
];

// Looping icon animation props per step
const stepIconAnimate = [
  { y: [0, -7, 0] },                           // MapPin drop
  { rotate: [-10, 10, -10] },                   // Paintbrush sway
  { rotate: [0, 18, -18, 0] },                  // Wrench tighten
  { y: [0, -9, 0], rotate: [-5, 5, -5] },       // Rocket ascend
];
const stepIconTransition = [
  { duration: 1.6, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.2 },
  { duration: 1.8, repeat: Infinity, ease: 'easeInOut' },
  { duration: 1.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.6 },
  { duration: 2.0, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.8 },
];

// ─── StepCard ─────────────────────────────────────────────────────────────────

function StepCard({
  Icon, num, title, body, i, iconAnimate, iconTransition,
}: {
  Icon: React.ElementType; num: string; title: string; body: string; i: number;
  iconAnimate: object; iconTransition: object;
}) {
  return (
    <motion.div
      className="relative group rounded-2xl bg-gray-950 border border-white/8 p-7 overflow-hidden cursor-default"
      whileHover={{ y: -4, boxShadow: '0 8px 40px rgba(212,175,55,0.14), 0 0 0 1px rgba(212,175,55,0.22)' }}
      transition={{ duration: 0.28 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
      <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-gold-500/8 blur-2xl group-hover:bg-gold-500/18 transition-all duration-700" />

      <div className="relative z-10 flex items-start justify-between mb-5">
        <div className="relative w-11 h-11">
          <div className="absolute inset-0 rounded-xl bg-gold-500/20 group-hover:bg-gold-500/30 transition-colors duration-500" />
          <div className="absolute inset-0 rounded-xl border border-gold-500/30 group-hover:border-gold-500/60 transition-colors duration-500" />
          <div className="absolute inset-0 rounded-xl bg-gold-500/10 blur-md group-hover:bg-gold-500/25 transition-all duration-500 scale-125" />
          <div className="relative w-full h-full flex items-center justify-center">
            <motion.div animate={iconAnimate} transition={iconTransition}>
              <Icon className="w-5 h-5 text-gold-400 group-hover:text-gold-300 transition-colors duration-300" />
            </motion.div>
          </div>
        </div>
        <span className="text-5xl font-bold leading-none select-none text-white/7 group-hover:text-white/12 transition-colors duration-500">{num}</span>
      </div>

      <div className="relative z-10">
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-white/45 text-sm leading-relaxed">{body}</p>
      </div>
    </motion.div>
  );
}

// ─── HomePage ─────────────────────────────────────────────────────────────────

function HomePage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === '#services') {
      setTimeout(() => {
        document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [hash]);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lastInteraction, setLastInteraction] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastInteraction >= 7000) {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setLastInteraction(Date.now());
      }
    }, 100);
    return () => clearInterval(interval);
  }, [lastInteraction]);

  const scrollToServices = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroSparkles = useMemo(() => (
    <div className="absolute inset-0">
      <SparklesCore
        id="hero-sparkles"
        background="transparent"
        minSize={0.6}
        maxSize={1.4}
        particleDensity={100}
        className="w-full h-full"
        particleColor="#D4AF37"
        speed={0.5}
      />
    </div>
  ), []);

  return (
    <div className="min-h-screen bg-white text-gray-900 relative overflow-x-hidden">
      <Helmet>
        <title>Terra Nexus AI | AI-Powered Business Solutions</title>
        <meta name="description" content="Transform your business with AI-powered solutions. Enhance customer interactions, reduce manual work, and build seamless digital experiences with Terra Nexus AI." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://terranexusai.com" />
        <meta property="og:title" content="Terra Nexus AI | AI-Powered Business Solutions" />
        <meta property="og:description" content="Transform your business with AI-powered solutions. Enhance customer interactions, reduce manual work, and build seamless digital experiences." />
        <meta property="og:url" content="https://terranexusai.com" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Navigation />

      {/* ─── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center px-4 bg-black text-white overflow-hidden">
        {heroSparkles}

        {/* Multi-layer ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full bg-gold-500/6 blur-[160px]" />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[460px] h-[340px] rounded-full bg-gold-400/7 blur-[90px]"
            animate={{ scale: [1, 1.18, 1], opacity: [0.55, 1, 0.55] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        {/* Subtle gold grid texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.028]"
          style={{
            backgroundImage: 'linear-gradient(rgba(212,175,55,1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,1) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />

        {/* Vertical light sweep */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold-400/18 to-transparent"
            initial={{ left: '-5%' }}
            animate={{ left: '105%' }}
            transition={{ duration: 5, delay: 1.8, ease: 'linear', repeat: Infinity, repeatDelay: 10 }}
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center">

          {/* Company name — word-by-word reveal */}
          <motion.div
            className="mb-6 flex flex-wrap items-baseline justify-center gap-x-5 gap-y-1"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } } }}
          >
            {[
              { word: 'TERRA', gold: false },
              { word: 'NEXUS', gold: false },
              { word: 'AI', gold: true },
            ].map(({ word, gold }) => (
              <motion.span
                key={word}
                className={`block text-[clamp(3.5rem,10vw,7rem)] font-bold tracking-tighter leading-none select-none ${
                  gold ? 'gold-gradient bg-clip-text text-transparent gold-glow' : 'text-white'
                }`}
                variants={{
                  hidden: { opacity: 0, y: 64, skewY: 4 },
                  visible: { opacity: 1, y: 0, skewY: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
                }}
              >
                {word}
              </motion.span>
            ))}
          </motion.div>

          {/* Animated gold separator */}
          <motion.div
            className="w-72 h-px bg-gradient-to-r from-transparent via-gold-500/65 to-transparent mb-7"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ duration: 1.1, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
          />

          {/* Slogan */}
          <motion.p
            className="text-white/45 text-base sm:text-lg md:text-xl max-w-md leading-relaxed mb-12 tracking-wide"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          >
            Unlock Time, Unlock Potential.
          </motion.p>

          {/* CTAs */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 1.25 }}
          >
            <Link to="/contact">
              <motion.button
                className="gold-gradient-btn flex items-center gap-2 px-9 py-4 rounded-full text-black font-semibold text-base"
                whileHover={{ scale: 1.04, boxShadow: '0 0 36px rgba(212,175,55,0.4)' }}
                whileTap={{ scale: 0.97 }}
              >
                Start a Project <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
            <motion.a
              href="#services"
              onClick={scrollToServices}
              className="flex items-center gap-2 px-9 py-4 rounded-full border border-white/15 text-white/65 hover:text-white hover:border-white/30 text-base font-medium transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              Explore Services <ChevronRight className="w-4 h-4" />
            </motion.a>
          </motion.div>

          {/* Scroll hint */}
          <motion.div
            className="mt-16 flex flex-col items-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.9 }}
          >
            <div className="w-px h-12 bg-gradient-to-b from-transparent to-gold-500/40" />
          </motion.div>
        </div>
      </section>

      {/* ─── SERVICES BENTO ──────────────────────────────────────────────────── */}
      <section id="services" className="relative z-10 py-28 px-4 bg-white overflow-hidden">
        {/* Subtle dot grid background */}
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            className="mb-16"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="text-gold-500 text-xs tracking-widest uppercase mb-3 font-semibold">What we build</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              One studio.{' '}
              <span className="gold-gradient bg-clip-text text-transparent">Four superpowers.</span>
            </h2>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">

            {/* Card 1 — Website (tall, spans 2 rows) — flies in from left */}
            <motion.div
              className="lg:row-span-2 relative group rounded-3xl bg-gray-950 border border-white/8 p-8 flex flex-col justify-between overflow-hidden min-h-[320px] lg:min-h-0 cursor-pointer"
              variants={flyInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
              whileHover={{ y: -6, boxShadow: '0 8px 40px rgba(212,175,55,0.18), 0 0 0 1px rgba(212,175,55,0.25)' }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold-500/15 blur-3xl group-hover:bg-gold-500/25 transition-all duration-700" />
              <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-gold-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10">
                <span className="inline-block px-3 py-1 rounded-full border border-gold-500/30 bg-gold-500/10 text-gold-400 text-xs tracking-wider mb-6 font-medium">Foundation</span>

                <div className="relative w-14 h-14 mb-6">
                  <div className="absolute inset-0 rounded-2xl bg-gold-500/20 group-hover:bg-gold-500/30 transition-colors duration-500" />
                  <div className="absolute inset-0 rounded-2xl border border-gold-500/30 group-hover:border-gold-500/60 transition-colors duration-500" />
                  <div className="absolute inset-0 rounded-2xl bg-gold-500/10 blur-md group-hover:bg-gold-500/25 transition-all duration-500 scale-125" />
                  <div className="relative w-full h-full flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
                    >
                      <Globe className="w-7 h-7 text-gold-400 group-hover:text-gold-300 transition-colors duration-300" />
                    </motion.div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-3">Website Design & Development</h3>
                <p className="text-white/50 leading-relaxed text-sm">
                  High-converting sites with AI built in from day one. Fast, custom, and designed around how your customers actually behave.
                </p>
              </div>
            </motion.div>

            {/* Card 2 — Chatbots — flies in from top-right */}
            <motion.div
              className="relative group rounded-3xl bg-gray-950 border border-white/8 p-8 flex flex-col overflow-hidden min-h-[260px] cursor-pointer"
              variants={flyInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.08}
              whileHover={{ y: -6, boxShadow: '0 8px 40px rgba(20,184,166,0.12), 0 0 0 1px rgba(20,184,166,0.2)' }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-teal-500/10 blur-3xl group-hover:bg-teal-500/20 transition-all duration-700" />

              <div className="relative z-10">
                <span className="inline-block px-3 py-1 rounded-full border border-teal-500/25 bg-teal-500/8 text-teal-400 text-xs tracking-wider mb-5 font-medium">Engagement</span>

                <div className="relative w-14 h-14 mb-5">
                  <div className="absolute inset-0 rounded-2xl bg-teal-500/15 group-hover:bg-teal-500/25 transition-colors duration-500" />
                  <div className="absolute inset-0 rounded-2xl border border-teal-500/25 group-hover:border-teal-500/50 transition-colors duration-500" />
                  <div className="absolute inset-0 rounded-2xl bg-teal-500/8 blur-md group-hover:bg-teal-500/20 transition-all duration-500 scale-125" />
                  <div className="relative w-full h-full flex items-center justify-center">
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2.0, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <MessageCircle className="w-7 h-7 text-teal-400 group-hover:text-teal-300 transition-colors duration-300" />
                    </motion.div>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2">AI Chatbots</h3>
                <p className="text-white/50 leading-relaxed text-sm">
                  Always-on chat that answers questions, qualifies leads, and books appointments — without your team lifting a finger.
                </p>
              </div>
            </motion.div>

            {/* Card 3 — Phone Agents — flies in from right with delay */}
            <motion.div
              className="relative group rounded-3xl bg-gray-950 border border-white/8 p-8 flex flex-col overflow-hidden min-h-[260px] cursor-pointer"
              variants={flyInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.18}
              whileHover={{ y: -6, boxShadow: '0 8px 40px rgba(59,130,246,0.12), 0 0 0 1px rgba(59,130,246,0.2)' }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-blue-500/10 blur-3xl group-hover:bg-blue-500/20 transition-all duration-700" />

              <div className="relative z-10">
                <span className="inline-block px-3 py-1 rounded-full border border-blue-500/25 bg-blue-500/8 text-blue-400 text-xs tracking-wider mb-5 font-medium">Voice AI</span>

                <div className="relative w-14 h-14 mb-5">
                  <div className="absolute inset-0 rounded-2xl bg-blue-500/15 group-hover:bg-blue-500/25 transition-colors duration-500" />
                  <div className="absolute inset-0 rounded-2xl border border-blue-500/25 group-hover:border-blue-500/50 transition-colors duration-500" />
                  <div className="absolute inset-0 rounded-2xl bg-blue-500/8 blur-md group-hover:bg-blue-500/20 transition-all duration-500 scale-125" />
                  <div className="relative w-full h-full flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: [0, -18, 18, -12, 12, 0] }}
                      transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 2.5 }}
                    >
                      <Phone className="w-7 h-7 text-blue-400 group-hover:text-blue-300 transition-colors duration-300" />
                    </motion.div>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2">AI Phone Agents</h3>
                <p className="text-white/50 leading-relaxed text-sm">
                  A natural-sounding voice agent that handles inbound calls, takes messages, and routes customers 24/7.
                </p>
              </div>
            </motion.div>

            {/* Card 4 — Automations — flies up from bottom */}
            <motion.div
              className="lg:col-span-2 relative group rounded-3xl bg-gray-950 border border-white/8 p-8 overflow-hidden min-h-[200px] cursor-pointer"
              variants={flyInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.1}
              whileHover={{ y: -6, boxShadow: '0 8px 40px rgba(212,175,55,0.15), 0 0 0 1px rgba(212,175,55,0.22)' }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute -bottom-16 right-16 w-56 h-56 rounded-full bg-gold-500/12 blur-3xl group-hover:bg-gold-500/22 transition-all duration-700" />
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-gold-500/4 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-7">
                <div className="flex-shrink-0">
                  <span className="inline-block px-3 py-1 rounded-full border border-gold-500/30 bg-gold-500/10 text-gold-400 text-xs tracking-wider mb-4 font-medium">Backend</span>
                  <div className="relative w-14 h-14">
                    <div className="absolute inset-0 rounded-2xl bg-gold-500/20 group-hover:bg-gold-500/30 transition-colors duration-500" />
                    <div className="absolute inset-0 rounded-2xl border border-gold-500/30 group-hover:border-gold-500/60 transition-colors duration-500" />
                    <div className="absolute inset-0 rounded-2xl bg-gold-500/10 blur-md group-hover:bg-gold-500/25 transition-all duration-500 scale-125" />
                    <div className="relative w-full h-full flex items-center justify-center">
                      <motion.div
                        animate={{ scale: [1, 1.25, 1], opacity: [1, 0.65, 1] }}
                        transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <Zap className="w-7 h-7 text-gold-400 group-hover:text-gold-300 transition-colors duration-300" />
                      </motion.div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Business Automations</h3>
                  <p className="text-white/50 leading-relaxed text-sm max-w-xl">
                    Connect your tools. Automate follow-ups, notifications, scheduling, and CRM updates so nothing slips through the cracks.
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ─── PROCESS ──────────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-28 px-4 bg-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.022] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            className="mb-20"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="text-gold-500 text-xs tracking-widest uppercase mb-3 font-semibold">How we work</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              How we <span className="gold-gradient bg-clip-text text-transparent">build.</span>
            </h2>
          </motion.div>

          {/* ── Left-spine timeline ── */}
          <div className="relative pl-8 md:pl-16">

            {/* Spine line */}
            <div className="absolute left-[15px] md:left-[27px] top-0 bottom-0 w-px bg-gray-100 overflow-hidden">
              <motion.div
                className="w-full h-full origin-top"
                style={{ background: 'linear-gradient(180deg, #D4AF37 0%, #3B82F6 40%, #10B981 70%, rgba(16,185,129,0.1) 100%)' }}
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              />
            </div>

            {(() => {
              const stepColors = [
                {
                  num: 'text-yellow-400',
                  numGlow: 'rgba(212,175,55,0.9)',
                  nodeBorder: 'border-yellow-400',
                  nodeRing1: 'border-yellow-400/70',
                  nodeRing2: 'border-yellow-400/40',
                  icon: 'text-yellow-400',
                  iconHover: 'text-yellow-200',
                  badgeBg: 'bg-yellow-400/25 group-hover:bg-yellow-400/45',
                  badgeBorder: 'border-yellow-400/50 group-hover:border-yellow-400/80',
                  badgeGlow: 'bg-yellow-400/15 group-hover:bg-yellow-400/35',
                  hoverShadow: '0 8px 40px rgba(212,175,55,0.45), 0 0 0 1px rgba(212,175,55,0.55)',
                  hoverShimmer: 'from-yellow-400/20',
                  topGlow: 'bg-yellow-400/15 group-hover:bg-yellow-400/35',
                  dividerHover: 'group-hover:bg-yellow-400/50',
                  ringColor1: 'rgba(212,175,55,0.8)',
                  ringColor2: 'rgba(212,175,55,0.45)',
                },
                {
                  num: 'text-blue-400',
                  numGlow: 'rgba(59,130,246,0.9)',
                  nodeBorder: 'border-blue-400',
                  nodeRing1: 'border-blue-400/70',
                  nodeRing2: 'border-blue-400/40',
                  icon: 'text-blue-400',
                  iconHover: 'text-blue-200',
                  badgeBg: 'bg-blue-400/25 group-hover:bg-blue-400/45',
                  badgeBorder: 'border-blue-400/50 group-hover:border-blue-400/80',
                  badgeGlow: 'bg-blue-400/15 group-hover:bg-blue-400/35',
                  hoverShadow: '0 8px 40px rgba(59,130,246,0.45), 0 0 0 1px rgba(59,130,246,0.55)',
                  hoverShimmer: 'from-blue-400/20',
                  topGlow: 'bg-blue-400/15 group-hover:bg-blue-400/35',
                  dividerHover: 'group-hover:bg-blue-400/50',
                  ringColor1: 'rgba(59,130,246,0.8)',
                  ringColor2: 'rgba(59,130,246,0.45)',
                },
                {
                  num: 'text-emerald-400',
                  numGlow: 'rgba(16,185,129,0.9)',
                  nodeBorder: 'border-emerald-400',
                  nodeRing1: 'border-emerald-400/70',
                  nodeRing2: 'border-emerald-400/40',
                  icon: 'text-emerald-400',
                  iconHover: 'text-emerald-200',
                  badgeBg: 'bg-emerald-400/25 group-hover:bg-emerald-400/45',
                  badgeBorder: 'border-emerald-400/50 group-hover:border-emerald-400/80',
                  badgeGlow: 'bg-emerald-400/15 group-hover:bg-emerald-400/35',
                  hoverShadow: '0 8px 40px rgba(16,185,129,0.45), 0 0 0 1px rgba(16,185,129,0.55)',
                  hoverShimmer: 'from-emerald-400/20',
                  topGlow: 'bg-emerald-400/15 group-hover:bg-emerald-400/35',
                  dividerHover: 'group-hover:bg-emerald-400/50',
                  ringColor1: 'rgba(16,185,129,0.8)',
                  ringColor2: 'rgba(16,185,129,0.45)',
                },
                {
                  num: 'text-rose-400',
                  numGlow: 'rgba(244,63,94,0.9)',
                  nodeBorder: 'border-rose-400',
                  nodeRing1: 'border-rose-400/70',
                  nodeRing2: 'border-rose-400/40',
                  icon: 'text-rose-400',
                  iconHover: 'text-rose-200',
                  badgeBg: 'bg-rose-400/25 group-hover:bg-rose-400/45',
                  badgeBorder: 'border-rose-400/50 group-hover:border-rose-400/80',
                  badgeGlow: 'bg-rose-400/15 group-hover:bg-rose-400/35',
                  hoverShadow: '0 8px 40px rgba(244,63,94,0.45), 0 0 0 1px rgba(244,63,94,0.55)',
                  hoverShimmer: 'from-rose-400/20',
                  topGlow: 'bg-rose-400/15 group-hover:bg-rose-400/35',
                  dividerHover: 'group-hover:bg-rose-400/50',
                  ringColor1: 'rgba(244,63,94,0.8)',
                  ringColor2: 'rgba(244,63,94,0.45)',
                },
              ];

              return (
                <div className="flex flex-col gap-6">
                  {steps.map(({ icon: Icon, num, title, body }, i) => {
                    const c = stepColors[i];
                    return (
                      <motion.div
                        key={num}
                        className="relative flex items-start gap-6 md:gap-10"
                        variants={flyInRight}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        custom={i * 0.12}
                      >
                        {/* Node */}
                        <div className="absolute -left-8 md:-left-16 top-6 flex-shrink-0">
                          <motion.div
                            className={`relative w-[30px] h-[30px] md:w-[54px] md:h-[54px] rounded-full bg-white border-2 ${c.nodeBorder} flex items-center justify-center shadow-[0_0_0_4px_white] md:shadow-[0_0_0_6px_white]`}
                            initial={{ scale: 0, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ type: 'spring', stiffness: 280, damping: 20, delay: i * 0.18 + 0.4 }}
                          >
                            <motion.div animate={stepIconAnimate[i]} transition={stepIconTransition[i]}>
                              <Icon className={`w-3.5 h-3.5 md:w-6 md:h-6 ${c.icon}`} />
                            </motion.div>
                            <motion.div
                              className={`absolute inset-0 rounded-full border ${c.nodeRing1}`}
                              animate={{ scale: [1, 1.9], opacity: [0.55, 0] }}
                              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut', delay: i * 0.6 }}
                            />
                            <motion.div
                              className={`absolute inset-0 rounded-full border ${c.nodeRing2}`}
                              animate={{ scale: [1, 2.7], opacity: [0.35, 0] }}
                              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut', delay: i * 0.6 + 0.5 }}
                            />
                          </motion.div>
                        </div>

                        {/* Card — full width */}
                        <motion.div
                          className="flex-1 group relative rounded-2xl bg-gray-950 border border-white/8 overflow-hidden"
                          whileHover={{ y: -3, boxShadow: c.hoverShadow }}
                          transition={{ duration: 0.25 }}
                        >
                          <div className={`absolute inset-0 bg-gradient-to-br ${c.hoverShimmer} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
                          <div className={`absolute -top-10 -right-10 w-36 h-36 rounded-full ${c.topGlow} blur-2xl transition-all duration-700`} />

                          <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-5 p-6 md:p-8">
                            {/* Step number accent */}
                            <div className="flex-shrink-0 md:w-20 md:text-right">
                              <span className={`text-5xl md:text-6xl font-bold leading-none select-none tabular-nums ${c.num} opacity-25 group-hover:opacity-45 transition-opacity duration-500`}
                                style={{ textShadow: `0 0 32px ${c.numGlow}` }}>
                                {num}
                              </span>
                            </div>

                            {/* Divider */}
                            <div className={`hidden md:block w-px self-stretch bg-white/6 ${c.dividerHover} transition-colors duration-500`} />

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg md:text-xl font-bold text-white mb-2">{title}</h3>
                              <p className="text-white/45 text-sm leading-relaxed">{body}</p>
                            </div>

                            {/* Icon badge */}
                            <div className="hidden md:flex flex-shrink-0">
                              <div className="relative w-12 h-12">
                                <div className={`absolute inset-0 rounded-xl ${c.badgeBg} transition-colors duration-500`} />
                                <div className={`absolute inset-0 rounded-xl border ${c.badgeBorder} transition-colors duration-500`} />
                                <div className={`absolute inset-0 rounded-xl ${c.badgeGlow} blur-md transition-all duration-500 scale-125`} />
                                <div className="relative w-full h-full flex items-center justify-center">
                                  <motion.div animate={stepIconAnimate[i]} transition={stepIconTransition[i]}>
                                    <Icon className={`w-5 h-5 ${c.icon} group-hover:${c.iconHover} transition-colors duration-300`} />
                                  </motion.div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* ─── OUR WORK ─────────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="mb-12"
            variants={flyInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            <p className="text-gold-500 text-xs tracking-widest uppercase mb-3 font-semibold">Portfolio</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Our <span className="gold-gradient bg-clip-text text-transparent">work.</span>
            </h2>
          </motion.div>

          <motion.div
            variants={flyInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0.1}
          >
            <div
              className="relative overflow-hidden rounded-3xl border border-white/8 bg-gray-950"
              style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}
            >
              <div
                className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {slides.map((slide) => (
                  <div key={slide.title} className="w-full flex-shrink-0">
                    <div className="p-6 md:p-8">
                      <div className="flex items-center justify-between mb-5">
                        <div>
                          <h3 className="text-xl font-bold text-white">{slide.title}</h3>
                          <p className="text-white/40 text-sm">{slide.subtitle}</p>
                        </div>
                        <a
                          href={slide.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold-500/30 bg-gold-500/10 text-gold-400 text-sm hover:bg-gold-500/20 hover:border-gold-500/50 transition-all duration-300"
                        >
                          View Live <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                      <motion.img
                        src={slide.image}
                        alt={slide.title}
                        className="w-full h-auto rounded-2xl shadow-2xl"
                        whileHover={{ scale: 1.015 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center gap-2 pb-6">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setCurrentSlide(i); setLastInteraction(Date.now()); }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === currentSlide ? 'w-8 bg-gold-400' : 'w-3 bg-white/15'}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── WHO WE HELP ──────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-24 px-4 bg-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.022] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            className="text-center mb-14"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="text-gold-500 text-xs tracking-widest uppercase mb-3 font-semibold">Who we serve</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Built for real{' '}
              <span className="gold-gradient bg-clip-text text-transparent">local businesses.</span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">
              We work with businesses that have real customers, real operations, and real need for systems that actually work.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {clients.map((name, i) => (
              <motion.div
                key={name}
                className="group relative rounded-2xl bg-gray-950 border border-white/8 p-5 text-center overflow-hidden cursor-default"
                variants={popIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.07}
                whileHover={{ y: -5, scale: 1.04, boxShadow: '0 6px 24px rgba(212,175,55,0.18), 0 0 0 1px rgba(212,175,55,0.26)' }}
                transition={{ duration: 0.25 }}
              >
                {/* Gold shimmer sweep on hover */}
                <div className="absolute inset-0 bg-gradient-to-tr from-gold-500/0 via-gold-500/8 to-gold-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 rounded-2xl" />
                <span className="relative z-10 text-white/60 group-hover:text-white text-sm font-medium transition-colors duration-300">{name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-28 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="relative rounded-3xl overflow-hidden text-center"
            variants={flyInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            {/* Dark panel */}
            <div className="absolute inset-0 bg-gray-900 rounded-3xl" />
            <div className="absolute inset-0 bg-gradient-to-br from-gold-500/15 via-transparent to-transparent rounded-3xl" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" />

            {/* Pulsing glow */}
            <motion.div
              className="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 bg-gold-500/8 rounded-full blur-3xl"
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute -bottom-16 right-16 w-48 h-48 bg-gold-500/6 rounded-full blur-3xl"
              animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
            />

            <div className="relative px-8 py-16 md:py-20 md:px-16">
              <motion.p
                className="text-gold-400/70 text-xs tracking-widest uppercase mb-4 font-semibold"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={0.15}
              >
                Ready when you are
              </motion.p>
              <motion.h2
                className="text-4xl md:text-5xl font-bold text-white mb-6 leading-snug"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={0.25}
              >
                Let's build the system your{' '}
                <span className="gold-gradient bg-clip-text text-transparent">business should already have.</span>
              </motion.h2>
              <motion.p
                className="text-white/50 text-lg mb-10 max-w-2xl mx-auto leading-relaxed"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={0.35}
              >
                Whether you need a better website, a chatbot, an AI phone agent, or backend automations,
                we can help turn the idea into a working system.
              </motion.p>
              <motion.div
                variants={popIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={0.45}
              >
                <Link to="/contact">
                  <motion.button
                    className="gold-gradient-btn inline-flex items-center gap-2 px-10 py-4 rounded-full text-black font-semibold text-base"
                    whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(212,175,55,0.45)' }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Start a Conversation <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="relative z-10 bg-gray-950 border-t border-white/8 py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <motion.div
              className="md:col-span-1"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
            >
              <p className="text-white font-bold text-lg mb-3">Terra Nexus AI</p>
              <p className="text-white/40 text-sm leading-relaxed">
                A creative AI studio helping local businesses run cleaner, faster, and smarter.
              </p>
            </motion.div>
            {[
              {
                label: 'Navigate',
                links: [
                  { label: 'About', to: '/about', isLink: true },
                  { label: 'Services', href: '#services', isLink: false, onClick: scrollToServices },
                  { label: 'Contact', to: '/contact', isLink: true },
                ],
              },
              {
                label: 'Contact',
                items: ['joseph.c@terranexusai.com', '(587) 785-9804', 'Alberta, Canada'],
              },
              {
                label: 'Social',
                socials: [
                  { label: 'X (Twitter)', href: 'https://x.com/terranexusai' },

                  { label: 'Instagram', href: 'https://www.instagram.com/terranexusai/' },
                ],
              },
            ].map((col, ci) => (
              <motion.div
                key={col.label}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={0.08 + ci * 0.08}
              >
                <p className="text-white/30 text-xs tracking-widest uppercase mb-4 font-semibold">{col.label}</p>
                <ul className="space-y-3 text-white/50 text-sm">
                  {col.links?.map((l) =>
                    l.isLink ? (
                      <li key={l.label}><Link to={l.to!} className="hover:text-white transition-colors duration-200">{l.label}</Link></li>
                    ) : (
                      <li key={l.label}><a href={l.href} onClick={l.onClick} className="hover:text-white transition-colors duration-200">{l.label}</a></li>
                    )
                  )}
                  {col.items?.map((item) => <li key={item} className="break-all">{item}</li>)}
                  {col.socials?.map((s) => (
                    <li key={s.label}><a href={s.href} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">{s.label}</a></li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
          <div className="border-t border-white/8 pt-8 text-center text-white/25 text-sm">
            © 2025 Terra Nexus AI. All rights reserved.
          </div>
        </div>
      </footer>

      <ContactPopup
        isOpen={isContactPopupOpen}
        onClose={() => setIsContactPopupOpen(false)}
      />
    </div>
  );
}

function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<ContactForm />} />
        <Route path="/about" element={<AboutPage />} />
      </Routes>
      <ConvoCoreWidget />
    </>
  );
}

export default App;
